import React, { useRef, useState } from 'react';
import { Button, Grid, TextField, MenuItem, Box } from '@mui/material';
import { useReactToPrint } from 'react-to-print';
import { MonthYearSelector } from '../month/monthYearSelector';
import PrintableView from './printableView';
import GeoCalendar from '../geoCalendar';
import { CalendarViewsProvider } from '../view/calendarViewsProvider';
import { colorNameToHex } from '../../../../util/colorMap';
const HolidayColors = {
  NATIONAL: colorNameToHex.DeepSkyBlue,
  RELIGIOUS: colorNameToHex.Purple,
  ANNUAL_CELEBRATION: colorNameToHex.Coral,
};

const HolidayLabels = {
  NATIONAL: { en: 'National', am: 'ብሔራዊ' },
  RELIGIOUS: { en: 'Religious', am: 'ሃይማኖታዊ' },
  ANNUAL_CELEBRATION: { en: 'Annual Celebration', am: 'ዓመታዊ' },
}; 

const CountryCodeLegend = () => {
  const countryCodes = [
    { code: 'ET', en: 'Ethiopian Holiday', am: 'የኢትዮጵያ በዓል' },
    { code: 'US', en: 'United States Holiday', am: 'የአሜሪካ በዓል' },
  ];

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '0.25rem',
        fontSize: '0.45rem',
        justifyContent: 'flex-start',
        marginTop: '0.1rem',
        pageBreakInside: 'avoid',
      }}
    >
      {countryCodes.map(({ code, en, am }) => (
        <div key={code} style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <span style={{ fontWeight: 'bold' }}>[{code}]</span>
            <span>{en}</span>
          </div>
          <div style={{ paddingLeft: '1.6rem', fontSize: '0.45rem', color: '#555' }}>{am}</div>
        </div>
      ))}
    </div>
  );
};



const HolidayLegend = () => {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.25rem',
        fontSize: '0.5rem',
        justifyContent: 'flex-start',
        marginTop: '0.1rem',
        pageBreakInside: 'avoid',
      }}
    >
      {Object.entries(HolidayColors).map(([key, color]) => {
        const label = HolidayLabels[key];
        return (
          <div
            key={key}
            style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                backgroundColor: color,
                borderRadius: 2,
                border: '1px solid #aaa',
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span>{label.en}</span>
              <span style={{ fontSize: '0.5rem', color: '#555' }}>{label.am}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};


const LegendFooter = () => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        pageBreakInside: 'avoid',
      }}
    >
      <HolidayLegend /> 
      <CountryCodeLegend />
    </div>
  );
};


const PrintView = () => {
  const componentRef = useRef();
  const [settings, setSettings] = useState({
    height: '8.5in',
    width: '11in',
    theme: 'light',
  });
  const printPreviewHeight = useRef(0);

  const [isPreview, setIsPreview] = useState(0);
  const [, forceRender] = useState(0);

  const handleSettingChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const togglePreview = () => {
    setIsPreview((prev) => !prev);
  };

  const handleTriggerPrint = () => {
    printPreviewHeight.current = settings.height;
    forceRender(n => n + 1); // Trigger re-render
    setTimeout(() => {
      handlePrint();
    }, 100); // Wait for layout update
  };


  const handlePrint = useReactToPrint({
    content: () => {
      printPreviewHeight.current = settings.height;
      return componentRef.current;
    },
    onAfterPrint: () => {
      printPreviewHeight.current = 0;
    },
    pageStyle: `
    @page {
      size: ${settings.width} ${settings.height};
      margin: 0;
    }
  
    @media print {
      body {
        margin: 0;
        padding: 0;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
  
      .printable-calendar {
        padding: 0.25in 0.25in 0.1in 0.25in; /* reduce bottom padding */
        background: ${settings.theme === 'dark' ? '#333' : '#fff'};
        color: ${settings.theme === 'dark' ? '#fff' : '#222'};
      }
  
      .calendar-cell {
        background: #fff !important;
        box-sizing: border-box !important;
        border-right: 1px solid #222;
        border-bottom: 1px solid #222;
      }
  
      .calendar-cell.row-0 {
        border-top: 1px solid #222;
      } 
    }
  `
  });



  const viewOnRemoteTV = () => {
    const newWindow = window.open('', '_blank', 'width=800,height=600');
    if (newWindow) {
      newWindow.document.body.innerHTML = `
        <div style="background-color: ${settings.theme === 'dark' ? '#333' : '#fff'}; color: ${settings.theme === 'dark' ? '#fff' : '#000'};">
          <h3>Printable View</h3>
          <div>${componentRef.current ? componentRef.current.innerHTML : ''}</div>
        </div>
      `;
      newWindow.document.title = 'Printable View';
    }
  };

  return (
    <Grid
      container
      sx={{
        width: '100%',
        minHeight: '100vh', // ✅ allow content to grow
        overflowX: 'hidden', // Prevent horizontal scroll
        margin: 0,
        padding: 0,
      }}
    >
      {!isPreview && (
        <Grid
          item
          xs={12}
          sm={4}
          md={3}
          lg={2}
          sx={{
            backgroundColor: '#f9f9f9',
            height: '100%',
            padding: 2,
            borderRight: '1px solid #e0e0e0',
            boxSizing: 'border-box',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <h3>Print Settings</h3>

            <TextField
              label="Height"
              name="height"
              value={settings.height}
              onChange={handleSettingChange}
              fullWidth
              helperText="e.g., 8.5in"
            />
            <TextField
              label="Width"
              name="width"
              value={settings.width}
              onChange={handleSettingChange}
              fullWidth
              helperText="e.g., 11in"
            />
            <TextField
              select
              label="Theme"
              name="theme"
              value={settings.theme}
              onChange={handleSettingChange}
              fullWidth
            >
              <MenuItem value="light">Light</MenuItem>
              <MenuItem value="dark">Dark</MenuItem>
            </TextField>

            <Button variant="contained" onClick={handleTriggerPrint}>
              Print
            </Button>
          </Box>

          <Box sx={{ marginTop: 4 }}>
            <GeoCalendar />
          </Box>
        </Grid>
      )}

      <Grid
        item
        xs={12}
        sm={isPreview ? 12 : 8}
        md={isPreview ? 12 : 9}
        lg={isPreview ? 12 : 10}
        container
        direction="column"
        sx={{
          width: '100%',
          overflowX: 'hidden', // Prevent scroll
        }}
      >
        {!isPreview && (
          <Grid item sx={{ padding: '10px' }}>
            <MonthYearSelector />
          </Grid>
        )}
        <Grid
          item
          sx={{
            padding: '16px',
            width: '100%',
            overflow: 'visible', // ✅ ensures no internal scroll
          }}
        >
          {/* Button Row at Top */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              mb: 2,
            }}
          >
            <Button
              variant="contained"
              size="small"
              onClick={viewOnRemoteTV}
            >
              View on TV
            </Button>

            <Button
              variant="contained"
              size="small"
              onClick={togglePreview}
            >
              {isPreview ? 'Exit Preview' : 'Preview'}
            </Button>
          </Box>

          {/* Printable Content Area */}

          <div ref={componentRef} className="printable-calendar">
            <CalendarViewsProvider>
              <PrintableView printPreviewHeight={printPreviewHeight.current} />
              <LegendFooter />
            </CalendarViewsProvider>
          </div>
        </Grid>

      </Grid>
    </Grid>
  );
};



export default PrintView;
